import { getDefaultFullVenueHourlyRate } from './venuePricing';
import { getActualPerPersonPrice } from '../components/VenueDashboard/utils/bookingUtils';

const toCompareDate = (date) => {
  if (date instanceof Date && !Number.isNaN(date.getTime())) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }
  const ymd = String(date).split('T')[0];
  const [y, m, d] = ymd.split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  return new Date(y, m - 1, d);
};

export const filterPricingRulesForDate = (rules, date) => {
  const compareDate = toCompareDate(date);
  if (!compareDate || !Array.isArray(rules) || rules.length === 0) return [];

  return rules.filter((rule) => {
    if (!rule?.start_date || !rule?.end_date) return false;
    const [sYear, sMonth, sDay] = rule.start_date.split('-').map(Number);
    const [eYear, eMonth, eDay] = rule.end_date.split('-').map(Number);
    const startDate = new Date(sYear, sMonth - 1, sDay);
    const endDate = new Date(eYear, eMonth - 1, eDay);
    return compareDate >= startDate && compareDate <= endDate;
  });
};

/**
 * Full-venue calendar cell: dynamic hourly (date) → flat total → default hourly.
 * Matches sidebar priority when no time is selected yet (uses min booking hours for flat).
 */
export const getFullVenueCalendarPriceForDate = (
  date,
  { venue, dynamicPricing = [], flatPricing = [], minBookingHours = 2 } = {}
) => {
  const baseHourlyRate = getDefaultFullVenueHourlyRate(venue) ?? 45;
  const applicableRules = filterPricingRulesForDate(dynamicPricing, date);

  if (applicableRules.length > 0) {
    const candidates = [Number(baseHourlyRate)];
    applicableRules.forEach((rule) => {
      const p = Number(rule.price_per_hour);
      if (Number.isFinite(p)) candidates.push(p);
    });
    const price = Math.min(...candidates);
    return {
      price,
      priceKind: 'hourly',
      isDiscounted: Number.isFinite(baseHourlyRate) && price < baseHourlyRate,
      isPremium: Number.isFinite(baseHourlyRate) && price > baseHourlyRate
    };
  }

  const activeFlat = (flatPricing || []).filter((r) => r?.is_active !== false);
  const duration = Number(minBookingHours) > 0 ? Number(minBookingHours) : 2;
  const matchingFlat =
    activeFlat
      .filter((r) => duration >= (r.min_hours || 0))
      .sort((a, b) => (b.min_hours || 0) - (a.min_hours || 0))[0] ||
    activeFlat.sort((a, b) => (a.min_hours || 0) - (b.min_hours || 0))[0];

  if (matchingFlat?.flat_price != null) {
    const flatPrice = Number(matchingFlat.flat_price);
    const defaultTotal = Number(baseHourlyRate) * duration;
    return {
      price: flatPrice,
      priceKind: 'flat',
      isDiscounted: Number.isFinite(flatPrice) && Number.isFinite(defaultTotal) && flatPrice < defaultTotal,
      isPremium: false
    };
  }

  return {
    price: baseHourlyRate,
    priceKind: 'hourly',
    isDiscounted: false,
    isPremium: false
  };
};

/** Per-person calendar cell: dynamic per-person rules for date → default per-person price. */
export const getPerPersonCalendarPriceForDate = (
  date,
  { dynamicPricingPerPerson = [], defaultPerPersonPrice = null } = {}
) => {
  const base =
    defaultPerPersonPrice != null && defaultPerPersonPrice !== ''
      ? Number(defaultPerPersonPrice)
      : null;
  const fromRules = getActualPerPersonPrice(
    date,
    null,
    null,
    dynamicPricingPerPerson,
    defaultPerPersonPrice
  );
  const price = fromRules != null ? fromRules : base ?? 0;

  return {
    price,
    priceKind: 'per_person',
    isDiscounted: base != null && Number.isFinite(price) && price < base,
    isPremium: base != null && Number.isFinite(price) && price > base
  };
};

export const getCalendarPriceForDate = (date, options = {}) => {
  const mode = options.bookingMode === 'per_person' ? 'per_person' : 'exclusive';
  if (mode === 'per_person') {
    return getPerPersonCalendarPriceForDate(date, options);
  }
  return getFullVenueCalendarPriceForDate(date, options);
};

export const calendarHasAnyDiscountedDay = ({
  bookingMode = 'exclusive',
  venue,
  dynamicPricing = [],
  dynamicPricingPerPerson = [],
  defaultPerPersonPrice = null
} = {}) => {
  if (bookingMode === 'per_person') {
    const base =
      defaultPerPersonPrice != null && defaultPerPersonPrice !== ''
        ? Number(defaultPerPersonPrice)
        : null;
    if (base == null || !dynamicPricingPerPerson?.length) return false;
    return dynamicPricingPerPerson.some((rule) => {
      const p = Number(rule.price_per_person);
      return Number.isFinite(p) && p < base;
    });
  }
  const base = getDefaultFullVenueHourlyRate(venue);
  if (base == null || !dynamicPricing?.length) return false;
  return dynamicPricing.some((rule) => {
    const p = Number(rule.price_per_hour);
    return Number.isFinite(p) && p < base;
  });
};

export const selectedCalendarDateHasDiscount = ({
  bookingMode = 'exclusive',
  selectedDateYmd,
  venue,
  dynamicPricing = [],
  dynamicPricingPerPerson = [],
  defaultPerPersonPrice = null
} = {}) => {
  if (!selectedDateYmd) return false;
  const info = getCalendarPriceForDate(selectedDateYmd, {
    bookingMode,
    venue,
    dynamicPricing,
    dynamicPricingPerPerson,
    defaultPerPersonPrice
  });
  return Boolean(info.isDiscounted);
};

/** Dynamic rule hour coverage for discount copy under the calendar (full-venue or per-person rules). */
export const getDynamicPricingCoverageForDate = (date, rules = []) => {
  if (!date || !rules?.length) return null;

  const applicableRules = filterPricingRulesForDate(rules, date);
  if (applicableRules.length === 0) return null;

  const totalHours = applicableRules.reduce((total, rule) => {
    if (!rule.start_time && !rule.end_time) return total + 24;
    if (rule.start_time === '00:00:00' && rule.end_time === '00:00:00') return total + 24;
    if (rule.start_time && rule.end_time) {
      const startTime = new Date(`2000-01-01T${rule.start_time}`);
      const endTime = new Date(`2000-01-01T${rule.end_time}`);
      let duration = (endTime - startTime) / (1000 * 60 * 60);
      if (duration < 0) duration += 24;
      return total + duration;
    }
    return total + 24;
  }, 0);

  return totalHours > 23 ? 'all_hours' : 'select_hours';
};
